import { useEffect, useRef, RefObject } from 'react';

/**
 * Hook that detects clicks outside a referenced element.
 *
 * @param onClickAway - Callback function to execute when a click occurs outside the element
 * @returns A ref to attach to the element you want to monitor for outside clicks
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const clickAwayRef = useClickAway<HTMLDivElement>(() => setIsOpen(false));
 *
 *   return isOpen ? (
 *     <div ref={clickAwayRef}>
 *       Content that closes when clicking outside
 *     </div>
 *   ) : null;
 * };
 * ```
 */
export function useClickAway<T extends HTMLElement>(
  onClickAway: (event: MouseEvent | TouchEvent) => void
): RefObject<T | null> {
  const ref = useRef<T>(null);
  const savedCallback = useRef(onClickAway);

  // Keep callback reference fresh without re-running effect
  useEffect(() => {
    savedCallback.current = onClickAway;
  }, [onClickAway]);

  useEffect(() => {
    const handleClick = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        savedCallback.current(event);
      }
    };

    document.addEventListener('mouseup', handleClick);
    document.addEventListener('touchstart', handleClick);

    return () => {
      document.removeEventListener('mouseup', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, []);

  return ref;
}
